import { emptyNetworkSchematic, type NetworkSchematic, type SurveyV2 } from '@ghp/domain'
import { InfoNote } from '@ghp/ui'
import { PencilRuler } from 'lucide-react'

import {
  SchematicEditor,
  type SchematicEditorComponent,
  type SchematicExportHandler,
} from '@ghp/schematic-editor'
import s from './SchematicTab.module.css'

/**
 * Onglet « Schéma » — le plan du branchement, dessiné sur place par l'agent. Il vient entre
 * « Pièces jointes » et « Transmettre », comme dans la maquette validée.
 *
 * Cet onglet ne dessine rien : il fait la **couture** entre le relevé (`SurveyV2`, donc le
 * brouillon Dexie) et la surface d'édition. C'est ce partage qui rend l'éditeur remplaçable —
 * voir `schematic-editor-port.ts` dans `@ghp/schematic-editor`, qui porte le motif.
 *
 * ⚠️ **M11 range le dessin dans le relevé, pas dans une table à lui** : personne ne requête
 * l'intérieur d'un dessin. Le champ n'étant pas indexé, l'ajout n'a demandé **aucune migration
 * Dexie** — comme la carte Rendez-vous (#194) et le motif de renvoi (#169). Poser un
 * `db.version(n)` pour lui aurait couru le risque de collision que `offline/data-db.ts`
 * documente.
 *
 * ⚠️ Il n'y a **pas de bouton « Enregistrer »**, que la maquette porte pourtant en haut à
 * droite : la saisie est rangée dans Dexie à chaque geste (D21), donc il n'y a rien à
 * enregistrer. C'est l'écart déjà assumé par l'en-tête de l'éditeur — un bouton qui ne fait
 * rien laisserait croire à l'agent qu'il perd son dessin s'il l'oublie.
 */
export interface SchematicTabProps {
  /** Le relevé du brouillon : c'est lui qui porte le dessin (M11). */
  survey: SurveyV2
  /** Fiche transmise : le schéma se consulte, il ne se modifie plus (D4). */
  disabled?: boolean
  /** Le relevé complet, dessin mis à jour — la même voie d'écriture que l'onglet « Relevé ». */
  onChange?: (survey: SurveyV2) => void
  /**
   * L'image d'archive que la tablette vient de rendre (M11) : au conteneur de l'attacher au
   * contrôle comme média `schematic`.
   */
  onExport?: SchematicExportHandler
  /**
   * Quand l'image d'archive a été rendue, ou `null` s'il n'y en a pas — le seul retour visible
   * d'un export, le média `schematic` étant écarté des pièces jointes partout ailleurs.
   */
  exportedAt?: string | null
  /**
   * L'image jointe ne montre plus le dessin en cours — le conteneur la compare au relevé
   * (`hasOutdatedSchematicImage`), parce que c'est lui qui a les pièces sous la main.
   */
  exportOutdated?: boolean
  /**
   * La surface d'édition. Par défaut l'implémentation SVG maison ; la prop existe pour que la
   * frontière soit **exécutable** plutôt que déclarative — un test la franchit avec un double,
   * et le jour d'une bascule vers une bibliothèque tierce, c'est cette ligne qui change.
   */
  editor?: SchematicEditorComponent
}

export function SchematicTab({
  survey,
  disabled,
  onChange,
  onExport,
  exportedAt = null,
  exportOutdated = false,
  editor: Editor = SchematicEditor,
}: SchematicTabProps) {
  // ⚠️ Un dessin vide plutôt qu'`undefined` : la plupart des fiches n'en portent pas, et
  // l'absence ne doit pas se traiter dans chacune des trois couches qui le lisent.
  const schematic: NetworkSchematic = survey.schematic ?? emptyNetworkSchematic()

  /**
   * ⚠️ Le relevé est **étalé**, jamais remplacé : il porte aussi la saisie de l'onglet
   * « Relevé », qui vit dans le même champ du brouillon. Le remplacer effacerait une fiche
   * remplie ailleurs, sans erreur et sans que rien ne le dise.
   */
  function change(next: NetworkSchematic): void {
    onChange?.({ ...survey, schematic: next })
  }

  return (
    <div className={s.root}>
      <Editor
        value={schematic}
        disabled={disabled}
        onChange={change}
        onExport={onExport}
        exportedAt={exportedAt}
        exportOutdated={exportOutdated}
      />
      <InfoNote icon={<PencilRuler size={15} />}>
        Le schéma part avec le contrôle : le bureau le relit tel quel. Rien à enregistrer — chaque
        geste est rangé sur la tablette.
      </InfoNote>
    </div>
  )
}
